/**
 * Чистая логика вкладки «Техника» карточки хозяйства (AK-747, экран 5).
 *
 * Вынесено из компонента намеренно: подписи итога, сборка параметров запроса
 * и разбор пакетного ответа — это правила, а не разметка, и проверяться они
 * должны без монтирования страницы (тот же приём, что `fieldsParams` и
 * `farmCardTabNavigation` у карточки полей).
 */

/** Под-вкладки раздела. Делаем только «Единицы», остальные — заглушки. */
import { formatRuDateNumeric, pluralRu } from '@/lib/dateFmt';
import type { ToastKind } from '@/lib/toast';

export const MACHINERY_VIEWS = ['units', 'aggregates', 'service'] as const;
export type MachineryView = (typeof MACHINERY_VIEWS)[number];

/**
 * Под-вкладка из адреса. Незнакомое значение не роняет экран, а даёт
 * «Единицы» — тот же приём, что `resolveFarmCardTab` у верхнего ряда.
 */
export function resolveMachineryView(raw: unknown): MachineryView {
  return MACHINERY_VIEWS.includes(raw as MachineryView) ? (raw as MachineryView) : 'units';
}

/**
 * Группы техники канон-справочника (`reference.machinery_groups`, миграция 053).
 *
 * Подписи для фильтра «Тип: все» с макета. Держим здесь, а не в компоненте:
 * тот же словарь нужен и таблице (бейдж строки), и подписи итога, и три копии
 * одного словаря разъезжаются — это у нас уже случалось со справочниками.
 */
export const MACHINERY_GROUP_LABELS: Record<string, string> = {
  self_propelled: 'Самоходная',
  implement: 'Прицепные СХМ',
  aerial: 'Авиационная',
};

export function machineryGroupLabel(code: string | null | undefined): string {
  if (!code) return 'Без группы';
  return MACHINERY_GROUP_LABELS[code] ?? code;
}

export type MachineryQuery = {
  /** Хозяйство карточки — вкладка всегда про ОДНО хозяйство. */
  farmId: string;
  search?: string;
  group?: string | null;
  /** Чекбокс «Показывать списанные» с макета. */
  includeArchived?: boolean;
};

/** Сколько строк грузим за раз. Совпадает с потолком пачки — см. ниже. */
export const MACHINERY_PAGE = 200;

/**
 * Параметры запроса списка.
 *
 * `include_archived` уезжает только когда он ВКЛЮЧЁН: списанные — это
 * исключение, и адрес запроса не должен нести дефолт (то же правило, что у
 * вкладки «Обзор» в адресе карточки).
 */
export function machineryParams(query: MachineryQuery, offset = 0): Record<string, string> {
  const params: Record<string, string> = {
    farm_id: query.farmId,
    limit: String(MACHINERY_PAGE),
    offset: String(offset),
  };
  if (query.search?.trim()) params.q = query.search.trim();
  if (query.group) params.machinery_group = query.group;
  if (query.includeArchived) params.include_archived = 'true';
  return params;
}

/** Поле паспорта профиля — форма рисует его по описанию с сервера. */
export type ProfileField = {
  key: string;
  type: 'number' | 'bool' | 'enum' | 'text';
  label: string;
  unit?: string | null;
  help?: string | null;
  options?: { value: string; label: string }[] | null;
  from_catalog: boolean;
  /** Поле хранится своей колонкой таблицы, а не в паспорте: отправлять его
   *  надо отдельным полем запроса, иначе сервер отвечает 422. */
  column?: boolean;
  value?: string | number | boolean | null;
};

export type ModelProfile = {
  model_id: string;
  model_name: string | null;
  type_name: string | null;
  machinery_group: string | null;
  manufacturer_name: string | null;
  profile: string;
  profile_label: string;
  fields: ProfileField[];
};

export type MachineryRow = {
  id: string;
  name: string;
  ref_id: string | null;
  ref_name: string | null;
  type_name: string | null;
  machinery_group: string | null;
  reg_number: string | null;
  inventory_number: string | null;
  power_hp: number | null;
  fuel_rate_l_per_h: number | null;
  year_made: number | null;
  unit_count: number;
  attrs: Record<string, string | number | boolean>;
  profile: string | null;
  status: 'operational' | 'written_off';
  status_label: string;
  /** Причина списания «код: комментарий» (AK-764). У живой машины — null. */
  archive_reason: string | null;
  farm_id: string | null;
  farm_name: string | null;
};

export type MachineryListPage = {
  total: number;
  items: MachineryRow[];
};

export type MachineryCounts = {
  total: number;
  written_off: number;
  by_group: { machinery_group: string | null; machinery_group_name: string | null; total: number }[];
};

/**
 * Подпись «Тракторов 11 · комбайнов 7 · …» из макета — по РАЗБИВКЕ С СЕРВЕРА.
 *
 * Считать её по загруженным строкам нельзя: на экране страница, а подпись
 * говорит про парк хозяйства целиком (то же правило, что «итог считает
 * выборку, а не страницу» у полей). Пустой парк подписи не получает — «0
 * единиц техники» уже сказано в самой таблице.
 */
export function machineryBreakdown(counts: MachineryCounts | undefined): string | null {
  if (!counts || counts.total === 0) return null;
  const parts = counts.by_group
    .filter((g) => g.total > 0)
    .map((g) => `${g.machinery_group_name ?? machineryGroupLabel(g.machinery_group)} ${g.total}`);
  return parts.length ? parts.join(' · ') : null;
}

/** Строка «в N сцепках» — справка, а не действие (сцепки живут у холдинга). */
export function unitCountLabel(count: number): string {
  if (!count) return '—';
  const tail = count % 10 === 1 && count % 100 !== 11 ? 'сцепке' : 'сцепках';
  return `в ${count} ${tail}`;
}

/** Формы для `pluralRu` — своего склонения не заводим, оно уже есть в `dateFmt`. */
export const MACHINERY_FORMS = ['единица', 'единицы', 'единиц'] as const;

/** «1 единица» / «2 единицы» / «5 единиц». */
export function machineryPlural(count: number): string {
  return `${count} ${pluralRu(count, MACHINERY_FORMS)}`;
}

/**
 * Свежесть парка (AK-764, Р29) — то, что сервер знает про «когда этому верили».
 *
 * Две даты, а не одна: «месяц никто не трогал» и «месяц назад человек
 * посмотрел и сказал, что всё так» — противоположные состояния для того, кто
 * решает, доверять ли парку под план.
 */
export type FleetFreshness = {
  farm_id: string;
  fleet_size: number;
  last_change_at: string | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
  note: string | null;
  fresh_as_of: string | null;
  stale_days: number | null;
};

/** Через сколько дней шапка перестаёт считать парк свежим. */
export const FLEET_STALE_AFTER_DAYS = 90;

/**
 * Подпись шапки «Актуальность парка».
 *
 * Три состояния, а не два. Парк, которого никто ни разу не подтверждал и не
 * менял, — это НЕ «устарел» и не «свежий», это «неизвестно»: сказать про
 * пустую историю «данные устарели» значит обвинить человека в том, чего он не
 * делал. Отдельно называем, ЧТО именно за дата, — «правка» и «подтверждение»
 * читаются по-разному, и склеивать их в «обновлено» нельзя.
 */
export function fleetFreshnessLabel(
  freshness: FleetFreshness | undefined,
): { text: string; stale: boolean } {
  if (!freshness || !freshness.fresh_as_of) {
    return { text: 'Актуальность парка: не подтверждалась', stale: false };
  }
  const confirmedIsNewer =
    freshness.confirmed_at !== null && freshness.confirmed_at === freshness.fresh_as_of;
  const date = formatRuDateNumeric(freshness.fresh_as_of);
  const who = confirmedIsNewer && freshness.confirmed_by ? `, ${freshness.confirmed_by}` : '';
  const what = confirmedIsNewer ? 'подтверждён' : 'последняя правка';
  return {
    text: `Актуальность парка: ${what} ${date}${who}`,
    stale: (freshness.stale_days ?? 0) >= FLEET_STALE_AFTER_DAYS,
  };
}

/** Сколько единиц заводим разом. Потолок — тот же, что у сервера. */
export const MAX_CREATE_QUANTITY = 50;

/**
 * Разбор поля «Количество» — «пусто ≠ мусор», как и везде.
 *
 * Пустое поле значит «одна» (человек просто не трогал счётчик), а не ноль:
 * форма, отбивающая пустое поле по умолчанию, требует ввода там, где ответ и
 * так очевиден. Ноль и минус — отбиваем словами: «завести 0 машин» это не
 * действие, а опечатка.
 */
export function parseQuantity(raw: string): { value: number; error: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: 1, error: null };
  if (!/^\d+$/.test(trimmed)) {
    return { value: 1, error: 'Количество — целое число, например 3' };
  }
  const value = Number(trimmed);
  if (value < 1) return { value: 1, error: 'Заводим минимум одну единицу' };
  if (value > MAX_CREATE_QUANTITY) {
    return {
      value: 1,
      error: `За раз заводим не больше ${MAX_CREATE_QUANTITY} — больше обычно опечатка`,
    };
  }
  return { value, error: null };
}

export type BulkFailure = { id: string; code: string; message: string };
export type BulkResult = { updated: number; failed: BulkFailure[] };

export interface MachineryOutcome {
  kind: ToastKind;
  message: string;
}

/**
 * Группировка отказов ПО ПРИЧИНЕ — с именами машин.
 *
 * Раньше здесь стояло «И ещё N — по той же причине», и это было утверждение,
 * которое код не проверял: в одной пачке законно встречаются РАЗНЫЕ причины
 * («уже списана», «гос. номер занят», «нет доступа к хозяйству»). Теперь
 * причина называется столько раз, сколько их было, а имена машин при ней —
 * человек читает «что именно не поехало», а не идентификаторы.
 */
export function groupFailures(
  failed: readonly BulkFailure[],
  rows: readonly MachineryRow[] = [],
): string[] {
  const nameById = new Map(rows.map((r) => [r.id, r.name]));
  const byReason = new Map<string, string[]>();
  for (const item of failed) {
    const names = byReason.get(item.message) ?? [];
    names.push(nameById.get(item.id) ?? 'без названия');
    byReason.set(item.message, names);
  }
  return [...byReason.entries()].map(([reason, names]) => {
    // Три имени — предел читаемости строки тоста; остальные считаем числом.
    const shown = names.slice(0, 3).map((n) => `«${n}»`).join(', ');
    const rest = names.length > 3 ? ` и ещё ${names.length - 3}` : '';
    return `${shown}${rest} — ${reason}`;
  });
}

/**
 * Итог пакетного действия: ТОН и текст.
 *
 * Тон по исходу — то же правило, что у `bulkActions.bulkOutcome` карточки
 * полей: всё получилось → `success` (самоскрывается), совсем ничего →
 * `error`, наполовину → `warning`. Ключевое здесь именно тон, а не текст:
 * `success` гаснет через четыре секунды, и список неповезших машин исчез бы
 * у человека из-под носа ровно тогда, когда он ему и нужен.
 *
 * Своя функция, а не импорт соседней: у полей итог считает гектары и
 * ключуется `field_id`, у техники — `id` и без площадей; общая сигнатура
 * получилась бы из одних необязательных полей. Общее — правило тона, и оно
 * выписано здесь дословно.
 */
export function machineryBulkOutcome(
  result: BulkResult,
  doneLabel: string,
  emptyLabel: string,
  rows: readonly MachineryRow[] = [],
): MachineryOutcome {
  const failed = result.failed ?? [];
  const okPart = result.updated > 0 ? `${doneLabel}: ${machineryPlural(result.updated)}` : emptyLabel;
  const failPart = failed.length > 0 ? ` Не удалось: ${groupFailures(failed, rows).join('; ')}.` : '';
  const kind: ToastKind = result.updated === 0 ? 'error' : failed.length > 0 ? 'warning' : 'success';
  return { kind, message: `${okPart}.${failPart}` };
}
